import { useEffect, useId, useRef, type ReactNode } from 'react';

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** role="alertdialog" para avisos que requieren respuesta. */
  alert?: boolean;
}

// Diálogo modal sobre <dialog> nativo: bloquea el fondo, gestiona el foco y cierra con Escape.
// Al cerrar devuelve el foco al elemento que lo abrió.
export function Dialog({ open, title, onClose, children, footer, alert = false }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const returnFocus = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocus.current = document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (returnFocus.current instanceof HTMLElement) returnFocus.current.focus();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      role={alert ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      onCancel={(event) => {
        // El "cancel" de un <input type="file"> interno (selector cerrado sin elegir) también sube
        // hasta aquí: no debe cerrar el diálogo. Solo cuenta el Escape del propio <dialog>.
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        onClose();
      }}
    >
      {open ? (
        <div className="dialog-body">
          <h2 id={titleId} className="dialog-title">
            {title}
          </h2>
          <div className="dialog-content">{children}</div>
          {footer ? <div className="dialog-footer">{footer}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
